import type {
  ActionButtonConfig,
  DialogComponentProps,
} from '@brightdrop/bd-ui';
import { BDDialog } from '@brightdrop/bd-ui';
import type { ComponentProps } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  useCreateFeedbackMutation,
  useUploadFeedbackImageMutation,
} from '~/api/generated-api';
import { translateText } from '~/common/components/TranslatedLabel/TranslatedLabel';
import useToasts from '~/common/components/toasts/useToasts';

import {
  selectActingProfileContextIds,
  selectProfileId,
} from '~/store/profile/profileSlice.selectors';
import { FeedbackFormContent } from './FeedbackFormContent';
import useStyles from './FeedbackModal.styles';
import { useFeedbackModalAttachments } from './useFeedbackModalAttachments';
import { submitModalFeedbackWithUploads } from './utils/submitModalFeedbackWithUploads';

const TRANSLATION_KEYS = {
  title: 'feedback:cta.shareYourFeedback',
  send: 'feedback:cta.send',
  cancel: 'feedback:cta.cancel',
} as const;

export type FeedbackModalProps = {
  isOpen: boolean;
  onClose: () => void;
  draftMessage: string;
  onDraftChange: (value: string) => void;
  onSubmitSuccess: () => void;
};

const FeedbackModal = ({
  isOpen,
  onClose,
  draftMessage,
  onDraftChange,
  onSubmitSuccess,
}: FeedbackModalProps) => {
  const { classes } = useStyles();
  const { showSuccessToast } = useToasts();

  const userId = useSelector(selectProfileId);
  const { organizationsId, fleetsId } = useSelector(
    selectActingProfileContextIds
  );

  const [createFeedback, { isLoading: isCreatingFeedback }] =
    useCreateFeedbackMutation();
  const [uploadFeedbackImage] = useUploadFeedbackImageMutation();
  const [isUploadingImages, setIsUploadingImages] = useState(false);

  const {
    attachmentItems,
    uploadSizeError,
    handleAttachmentFilesSelected,
    handleAttachmentSizeError,
    handleRemoveAttachment,
    clearAttachments,
  } = useFeedbackModalAttachments();

  const localMessageRef = useRef(draftMessage);
  const [localMessage, setLocalMessage] = useState(draftMessage);
  const [starRating, setStarRating] = useState<number | undefined>(undefined);
  const [isError, setIsError] = useState(false);

  const isSubmitting = isCreatingFeedback || isUploadingImages;

  const prevIsOpen = useRef(isOpen);
  useEffect(() => {
    if (isOpen && !prevIsOpen.current) {
      localMessageRef.current = draftMessage;
      setLocalMessage(draftMessage);
      setStarRating(undefined);
      clearAttachments();
    }
    prevIsOpen.current = isOpen;
  }, [isOpen, draftMessage, clearAttachments]);

  const handleValueChange = useCallback(
    (next: string) => {
      localMessageRef.current = next;
      setLocalMessage(next);
      if (isError) setIsError(false);
    },
    [isError]
  );

  const handleClose = useCallback(() => {
    clearAttachments();
    onDraftChange(localMessageRef.current);
    setIsError(false);
    onClose();
  }, [onClose, onDraftChange, clearAttachments]);

  /** Latest form props for {@link renderFeedbackFormDialogBody}; see discount modal. */
  const formContentPropsRef = useRef<ComponentProps<
    typeof FeedbackFormContent
  > | null>(null);

  const formContentClassNames = useMemo(
    () => ({
      formContent: classes.formContent,
      formFieldsBlock: classes.formFieldsBlock,
      formFieldsBlockInner: classes.formFieldsBlockInner,
      ratingLabel: classes.ratingLabel,
      ratingRow: classes.ratingRow,
      messageLabel: classes.messageLabel,
      outlinedMessageField: classes.outlinedMessageField,
      supportDisclaimer: classes.supportDisclaimer,
      supportLink: classes.supportLink,
    }),
    [
      classes.formContent,
      classes.formFieldsBlock,
      classes.formFieldsBlockInner,
      classes.ratingLabel,
      classes.ratingRow,
      classes.messageLabel,
      classes.outlinedMessageField,
      classes.supportDisclaimer,
      classes.supportLink,
    ]
  );

  const handleSubmit = useCallback(async () => {
    const message = localMessageRef.current;
    if (!message.trim()) return;

    setIsError(false);

    try {
      setIsUploadingImages(true);
      try {
        await submitModalFeedbackWithUploads({
          message,
          starRating,
          userId,
          organizationsId: organizationsId ?? '',
          fleetsId: fleetsId ?? undefined,
          attachmentFiles: attachmentItems.map((item) => item.file),
          uploadFeedbackImage: (arg) => uploadFeedbackImage(arg).unwrap(),
          createFeedback,
        });
      } finally {
        setIsUploadingImages(false);
      }

      localMessageRef.current = '';
      setLocalMessage('');
      setStarRating(undefined);
      clearAttachments();
      onSubmitSuccess();
      showSuccessToast(translateText('feedback:cta.feedbackSent'), {
        testId: 'feedback-sent-toast',
      });
      handleClose();
    } catch {
      setIsError(true);
    }
  }, [
    organizationsId,
    fleetsId,
    userId,
    starRating,
    attachmentItems,
    createFeedback,
    uploadFeedbackImage,
    clearAttachments,
    onSubmitSuccess,
    showSuccessToast,
    handleClose,
  ]);

  /**
   * Must keep a **stable** function reference. BDDialog renders `component` as
   * `<Component />`, so a new function each render remounts the form and breaks
   * the controlled text field and draft sync.
   */
  const renderFeedbackFormDialogBody = useCallback(
    function renderFeedbackFormDialogBody(_: DialogComponentProps) {
      const p = formContentPropsRef.current;
      if (!p) return null;
      return <FeedbackFormContent {...p} />;
    },
    []
  );

  formContentPropsRef.current = {
    value: localMessage,
    isSubmitting,
    errorMessage: isError
      ? translateText('feedback:cta.submitError')
      : undefined,
    onValueChange: handleValueChange,
    starValue: starRating,
    onStarChange: setStarRating,
    classes: formContentClassNames,
    placeholder: translateText('feedback:cta.yourFeedbackPlaceholder'),
    commentsLabel: translateText('feedback:cta.commentsOptional'),
    onSupportNavigate: handleClose,
    attachmentItems,
    uploadSizeError,
    onAttachmentFilesSelected: handleAttachmentFilesSelected,
    onAttachmentSizeError: handleAttachmentSizeError,
    onRemoveAttachment: handleRemoveAttachment,
  };

  const formButtons: ActionButtonConfig[] = [
    {
      variant: 'tertiary',
      displayLabel: translateText(TRANSLATION_KEYS.cancel),
      accessibleLabel: translateText(TRANSLATION_KEYS.cancel),
      onClick: handleClose,
      id: 'feedback-modal-cancel-button',
    },
    {
      variant: 'primary',
      displayLabel: translateText(TRANSLATION_KEYS.send),
      accessibleLabel: translateText(TRANSLATION_KEYS.send),
      onClick: handleSubmit,
      disabled: !localMessage.trim() || isSubmitting,
      isLoading: isSubmitting,
      id: 'feedback-modal-submit-button',
    },
  ];

  return (
    <BDDialog
      openModal={isOpen}
      onCloseModal={handleClose}
      title={translateText(TRANSLATION_KEYS.title)}
      component={renderFeedbackFormDialogBody}
      buttonsConfig={formButtons}
      labels={{
        aria: translateText(TRANSLATION_KEYS.title),
      }}
      dataTestId="feedback-modal"
    />
  );
};

export default FeedbackModal;
